import {
  argbFromHex,
  argbFromRgba,
  Blend,
  ColorGroup,
  CorePalette,
  CustomColor,
  CustomColorGroup,
  Hct,
  hexFromArgb,
  rgbaFromArgb,
  TonalPalette,
} from '@material/material-color-utilities'

import { DEFAULT_MATERIAL_TYPOGRAPHY, MtTypography } from '@makyoapp/makyo-tw-ui'

import { MdShape } from './builder/types'
import { Scheme, SchemeProperties } from './scheme'

export type { MtTypography }

export const DEFAULT_EXTENDED_COLORS = [
  { _Name: 'Extended 1', _Value: -12627531, _Blend: false },
  { _Name: 'Extended 2', _Value: -16738680, _Blend: false },
  { _Name: 'Extended 3', _Value: -4941, _Blend: false },
  { _Name: 'Extended 4', _Value: -4267546, _Blend: false },
  { _Name: 'Extended 5', _Value: -476208, _Blend: false },
  { _Name: 'Extended 6', _Value: -4855081, _Blend: false },
  { _Name: 'Extended 7', _Value: -10453621, _Blend: false },
  { _Name: 'Extended 8', _Value: 4294309340, _Blend: false },
  { _Name: 'Extended 9', _Value: -14593, _Blend: false },
  { _Name: 'Extended 10', _Value: -9535, _Blend: false },
  { _Name: 'Extended 11', _Value: -4080722, _Blend: false },
  { _Name: 'Extended 12', _Value: -6190977, _Blend: false },
]

export interface BrandKitTheme {
  name: string
  theme: MultiSourceTheme
}

export interface Theme {
  source: number
  schemes: { light: Scheme; dark: Scheme }
  contentScheme?: { light: Scheme; dark: Scheme }
  palettes: {
    primary: TonalPalette
    secondary: TonalPalette
    tertiary: TonalPalette
    neutral: TonalPalette
    neutralVariant: TonalPalette
    error: TonalPalette
  }
  customColors: CustomColorGroup[]
}

export interface BrandKitColor {
  isDefault: boolean
  name: string
  value: number

  /**
   * Array of the user custom/extended colors
   * the format is
   * [
   *   {
   *     // argb integer value of the color
   *     value: number
   *
   *     // string name of the color
   *     // should be unique within this set of array
   *     // used as the main identifier later
   *     name: string
   *
   *     // option to harmonize custom color or not
   *     blend: boolean
   *   }
   * ]
   */
  customColors: CustomColor[]
}

export type ExtendedColor = {
  /** label */
  label: string
  /** argb value */
  argb: number
  /** hex value (converted) */
  hex: string
}

/**
 * brand kit
 */
export interface BrandKit {
  /**
   * primary unique ID
   */
  id: string
  /**
   * array of URL to the logos, this will have different URL
   * where the logos is uploaded
   */
  logos: string[]
  /**
   * the brand kit name
   */
  name: string
  /**
   * the material design version 3 typography settings
   * currently this part is not final, it's still in
   * development from google MD3
   *
   * @see MtTypography
   */
  typography: MtTypography
  /**
   * the makyo brand kit custom typography design
   * this will store all custom token for typography
   * that's not part of material design version 3.
   * but can be related to typography settings
   *
   * @see BkTypography
   */
  //typographyExtra: BkTypography
  /**
   * scheme object
   * this value is get from the generated material design version 3
   * this will be send from front end to backend to be save after generation
   */
  schemes?: Record<MtColorScheme, SchemeProperties>

  colors: BrandKitColor[]

  isDetailOk?: boolean
}

/**
 * the makyo brand kit custom typography design
 * this will store all custom token for typography
 * that's not part of material design version 3.
 * but can be related to typography settings
 */
export interface BkTypography {
  displayLarge: BkTypographyItem
  displayMedium: BkTypographyItem
  displaySmall: BkTypographyItem
  headlineLarge: BkTypographyItem
  headlineMedium: BkTypographyItem
  headlineSmall: BkTypographyItem
  titleLarge: BkTypographyItem
  titleMedium: BkTypographyItem
  titleSmall: BkTypographyItem
  labelLarge: BkTypographyItem
  labelMedium: BkTypographyItem
  labelSmall: BkTypographyItem
  bodyLarge: BkTypographyItem
  bodyMedium: BkTypographyItem
  bodySmall: BkTypographyItem
}

export interface BkTypographyBorderRadius {
  topLeft: [number, BkBorderRadiusUnit]
  topRight: [number, BkBorderRadiusUnit]
  bottomLeft: [number, BkBorderRadiusUnit]
  bottomRight: [number, BkBorderRadiusUnit]
}

export type BkCustomColorValue = `cc-${string}-${ColorGroupKey}` | `cc-${string}-source`

export type BkColorValue = number | SchemeJsonKey | BkCustomColorValue | 'auto' | 'transparent'

export type BkColorValueStr = SchemeJsonKey | BkCustomColorValue | 'auto' | 'transparent'

export interface BkMarginOrPadding {
  top: [number, BkLetterSpacingUnit]
  left: [number, BkLetterSpacingUnit]
  right: [number, BkLetterSpacingUnit]
  bottom: [number, BkLetterSpacingUnit]
}

export type BkLineHeightUnit = 'px' | 'em' | 'rem'
export type BkLetterSpacingUnit = BkLineHeightUnit | '%'
export type BkBorderRadiusUnit = 'px' | '%'
export type BkHorizontalAlign = 'left' | 'center' | 'right' | 'justify'
export type BkVerticalAlign = 'top' | 'middle' | 'bottom'
export type BkTextTransform = 'uppercase' | 'lowercase' | 'capitalize'
export type BorderStyle = 'solid' | 'dashed' | 'dotted'
/**
 * extra typography settings that's not part of
 * material design version 3
 */
export interface BkTypographyItem {
  /**
   * text color
   * scheme: light | dark
   * check `background` field for more detail about the color value
   */
  color?: Record<MtColorScheme, BkColorValue>
  /**
   * text horizontal alignment position
   * real value is in string with limited
   * options: 'left', 'center', 'right', 'justify'
   */
  hAlign?: BkHorizontalAlign
  /**
   * text vertical alignment position
   * real value is in string with limited
   * options: 'top', 'middle', 'bottom'
   */
  vAlign?: BkVerticalAlign
  /**
   * line height unit
   * real value is in string with limited
   * options: 'px', 'em', 'rem'
   */
  lhUnit: BkLineHeightUnit
  /**
   * letter spacing unit
   * real value is in string with limited
   * options: 'px', 'em', 'rem', '%'
   */
  lsUnit: BkLetterSpacingUnit
  /**
   * letter spacing unit
   * real value is in string with limited
   * options: 'uppercase', 'lowercase', 'capitalize'
   */
  textTransform?: BkTextTransform
  /**
   * background color
   * scheme: dark | light
   * raw value will be in object key value pair
   * eq: {
   *   dark: value,
   *   light: value
   * }
   *
   * the value is mixed of string and number
   * with limited options for string
   *
   * number | SchemeJsonKey | BkCustomColorValue | "auto" | "transparent"
   *
   * @see SchemeJsonKey
   * @see BkCustomColorValue
   *   - `cc-${ExtendedColorIdName}-color`
   *   - `cc-${ExtendedColorIdName}-onColor`
   *   - `cc-${ExtendedColorIdName}-colorContainer`
   *   - `cc-${ExtendedColorIdName}-onColorContainer`
   *   - `cc-${ExtendedColorIdName}-source`
   */
  background?: Record<MtColorScheme, BkColorValue>
  /**
   * percentage of opacity
   * min: 0
   * max: 100
   */
  backgroundOpacity: number
  /**
   * border radius value in pixel
   * the value is stored in object of
   *
   * borderRadius: {
   *   topLeft: number
   *   topRight: number
   *   bottomLeft: number
   *   bottomRight: number
   * }
   */
  borderRadius: BkTypographyBorderRadius
  /**
   * border width value in pixel
   * default: 0
   */
  borderWidth: number
  /**
   * the border color
   * see `background` field doc for color value
   */
  borderColor: Record<MtColorScheme, BkColorValue>
  /**
   * border direction flag number
   * raw value is stored in number of bit flag
   */
  borderDirection: BorderDirectionFlag
  /**
   * margin value
   */
  borderStyle: BorderStyle
  margin: BkMarginOrPadding
  padding: BkMarginOrPadding
}

export type MtColorScheme = 'light' | 'dark'

export enum BorderDirectionFlag {
  Left = 1,
  Right = 2,
  Top = 4,
  Bottom = 8,
}

/**
 * material design version 3 core typography settings
 */
export interface MtTypographyItem {
  /**
   * font family name
   */
  familyName: string
  /**
   * font style
   * the value is font family dependant
   */
  familyStyle: string
  /**
   * weight of the font
   * options: 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900
   */
  weight: number
  /**
   * font size
   */
  size: number
  /**
   * line height
   * note: this is not unit dependent
   * the unit can be set in `typographyExtra`
   * because by default MD3 only use pixel
   */
  lineHeight: number
  /**
   * letter spacing
   * note: this is not unit dependent
   * the unit can be set in `typographyExtra`
   * because by default MD3 only use pixel
   */
  letterSpacing: number

  /**
   * text transform
   * options: 'none' | 'uppercase' | 'lowercase' | 'capitalize'
   */
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize'
}

export type MtTypographyKey = keyof MtTypography

interface MtCSS {
  source: string
  dark: Record<string, string>
  light: Record<string, string>
  palette: Record<string, Record<string, string>>
  typography: Record<string, Record<string, string | number>>
}

export interface MultiSourceTheme extends Theme {
  secondarySource?: number
  tertiarySource?: number
  neutralSource?: number
  neutralVariantSource?: number
}

export type SchemeJsonKey = keyof SchemeProperties

export type ColorGroupKey = keyof ColorGroup
/**
 * get the css for a theme
 *
 * @param theme Theme object
 * @param options Options
 */
export function getApplyTheme(
  theme: MultiSourceTheme,
  options?: { paletteTones?: boolean; typography?: MtTypography }
) {
  const css: MtCSS = { source: hexFromArgb(theme.source), palette: {}, dark: {}, light: {}, typography: {} }

  css.dark = getSchemeProperties(
    theme.schemes.dark,
    '-dark',
    theme.customColors.map((cc) => {
      return { name: cc.color.name, value: cc.color.value, colors: cc.dark, source: cc.value }
    })
  )
  css.light = getSchemeProperties(
    theme.schemes.light,
    '-light',
    theme.customColors.map((cc) => {
      return { name: cc.color.name, value: cc.color.value, colors: cc.light, source: cc.value }
    })
  )

  if (options?.paletteTones !== false) {
    css.palette = getPaletteProperties(theme)
  }

  const typography = options?.typography ?? { ...DEFAULT_MATERIAL_TYPOGRAPHY }
  for (const ts of Object.keys(typography)) {
    const item = typography[ts as keyof MtTypography]
    const typeSize = ts.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
    //const type = split[0]
    //const size = split[1]

    css.typography[`${typeSize}`] = {
      [`--md-sys-typescale-${typeSize}`]: `${item.weight} ${convertToRem(Number(item.size))}rem / ${convertToRem(
        Number(item.lineHeight)
      )}rem "${item.familyName}"`,
    }

    for (const [k, v] of Object.entries(item)) {
      const token = k
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .toLowerCase()
        .replace('letter-spacing', 'tracking')

      const font = ['lineHeight', 'letterSpacing'].includes(k) ? '' : '-font'

      const mdToken = token.includes('family-name')
        ? `--md-sys-typescale-${typeSize}${font}`
        : `--md-sys-typescale-${typeSize}${font}-${token}`

      if (['familyName', 'familyStyle'].includes(k)) {
        css.typography[`${typeSize}`][mdToken] = v
      } else if (['textTransform'].includes(k)) {
        css.typography[`${typeSize}`][mdToken] = v.toLowerCase()
      } else {
        css.typography[`${typeSize}`][mdToken] = `${v}px`
      }
    }
  }

  return css
}

function getPaletteProperties(theme: Theme) {
  const tones = [0, 10, 20, 25, 30, 35, 40, 50, 60, 70, 80, 90, 95, 98, 99, 100]
  const palettes: Record<string, Record<string, string>> = {}
  for (const [key, palette] of Object.entries(theme.palettes)) {
    palettes[key] = {} as Record<string, string>
    const paletteKey = key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
    for (const tone of tones) {
      const token = `--md-ref-palette-${paletteKey}${tone}`
      palettes[key][token] = hexFromArgb(palette.tone(tone))
    }
  }
  return palettes
}

function getSchemeProperties(
  scheme: Scheme,
  suffix = '',
  customColors: { name: string; colors: ColorGroup; value: number; source: number }[] = []
) {
  const properties: Record<string, string> = {}
  let primary = ''
  for (const [k, v] of Object.entries(scheme.toJSON())) {
    const token = k.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
    const color = hexFromArgb(v)
    if (token === 'primary') {
      primary = color
    }
    properties[`--md-sys-color-${token}${suffix}`] = color
  }
  properties[`--md-sys-color-surface-tint${suffix}`] = primary

  for (const custom of customColors) {
    const name = custom.name.toLowerCase().replace(/\s/g, '-')
    for (const [k, v] of Object.entries(custom.colors)) {
      const token = k.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
      properties[`--md-sys-color-custom-${name}-${token}${suffix}`] = hexFromArgb(v)
      properties[`--md-sys-color-custom-${name}-source`] = hexFromArgb(custom.source)
    }
  }

  return properties
}

enum CSSKindFlag {
  Palette = 1,
  Scheme = 2,
  Active = 4,
  Typography = 8,
  Shape = 16,
  All = 1 | 2 | 4 | 8 | 16,
}

function hasCSSKindFlag(flags: CSSKindFlag, lookup: CSSKindFlag) {
  return (flags & lookup) === lookup
}

function cssObjToStr(css: MtCSS, mode: MtColorScheme = 'light', kind: CSSKindFlag = CSSKindFlag.All, inline = false) {
  let s = ''
  const idt = !inline ? '  ' : ''
  const nl = !inline ? '\n' : ''
  if (!inline) {
    s += ':root {\n'
    s += `${idt}--md-source: ${css.source};\n`
  }

  if (hasCSSKindFlag(kind, CSSKindFlag.Palette)) {
    s += palettesToStr(css.palette, inline)
  }

  if (hasCSSKindFlag(kind, CSSKindFlag.Scheme)) {
    for (const scheme of ['light', 'dark']) {
      if (!inline) {
        s += `${idt}/* ${scheme} */${nl}`
      }

      s += schemeToStr(css[scheme as MtColorScheme], false, inline)
    }
  }

  if (hasCSSKindFlag(kind, CSSKindFlag.Active)) {
    if (!inline) {
      s += `${idt}/* active scheme */\n`
    }
    s += schemeToStr(css[mode], true, inline)
  }

  if (hasCSSKindFlag(kind, CSSKindFlag.Typography)) {
    for (const [tk, tv] of Object.entries(css.typography)) {
      s += `${idt}/* ${tk.replace('-', ' - ')} */${nl}`

      for (const [token, value] of Object.entries(tv)) {
        s += `${idt}${token}: ${value};${nl}`
      }
    }
  }

  /*
  if (mode === 'dark') {
    // fix disabled colors
    if (inline) {
      //s += `${idt} fix disabled colors `
    }
    s +=
      '--md-filled-button-disabled-container-color: var(--md-sys-color-surface-variant);'
    s +=
      '--md-tonal-button-disabled-container-color: var(--md-sys-color-surface-variant);'
  }*/

  if (hasCSSKindFlag(kind, CSSKindFlag.Shape)) {
    s += shapeCss()
  }

  if (!inline) {
    s += '}\n'
  }
  if (hasCSSKindFlag(kind, CSSKindFlag.Typography) && !inline) {
    for (const [tk] of Object.entries(css.typography)) {
      s += `${nl}.${tk} {${nl}`
      s += `${idt}font: var(--md-sys-typescale-${tk});${nl}`
      s += `${idt}letter-spacing: var(--md-sys-typescale-${tk}-tracking);${nl}`
      s += `}${nl}`
    }
  }
  return s
}

function cssObjToProps(css: MtCSS, mode: MtColorScheme = 'light') {
  const obj: Record<string, string> = {}
  for (const [token, color] of Object.entries(css[mode])) {
    let tk = token
    tk = token.replace(/(-light|-dark)$/g, '')
    obj[tk] = color
  }
  return obj
}

function shapeCss() {
  return [
    '--md-sys-shape-corner-full: 9999px;',
    '--md-sys-shape-corner-extra-large: 28px;',
    '--md-sys-shape-corner-large: 16px;',
    '--md-sys-shape-corner-medium: 12px;',
    '--md-sys-shape-corner-small: 8px;',
    '--md-sys-shape-corner-extra-small: 4px;',
    '--md-sys-shape-corner-none: 0;',
  ].join('\n')
}

function palettesToStr(palettes: Record<string, Record<string, string>>, inline: boolean) {
  let s = ''
  const idt = !inline ? '  ' : ''
  const nl = !inline ? '\n' : ''
  for (const [k, v] of Object.entries(palettes)) {
    if (!inline) {
      s += `${idt}/* ${k.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()} */${nl}`
    }
    for (const [token, color] of Object.entries(v)) {
      s += `${idt}${token}: ${color};${nl}`
    }
  }
  return s
}

function schemeToStr(scheme: Record<string, string>, removeSuffix = false, inline: boolean) {
  let s = ''
  const idt = !inline ? '  ' : ''
  const nl = !inline ? '\n' : ''

  if (!scheme) return ''
  for (const [token, color] of Object.entries(scheme)) {
    let tk = token
    if (removeSuffix) {
      tk = token.replace(/(-light|-dark)$/g, '')
    }
    s += `${idt}${tk}: ${color};${nl}`
  }
  return s
}

export function themeFromMultiSource(
  sources: (number | undefined)[],
  customColors: CustomColor[] = []
): MultiSourceTheme {
  if (sources.length < 1) {
    throw new Error('required at least one source color')
  }

  const multiCorePalettes = CorePalette.fromColors({
    primary: sources[0]!,
    secondary: sources[1],
    tertiary: sources[2],
    neutral: sources[3],
    neutralVariant: sources[4],
  })

  return {
    source: sources[0]!,
    secondarySource: sources?.[1] || undefined,
    tertiarySource: sources?.[2] || undefined,
    neutralSource: sources?.[3] || undefined,
    neutralVariantSource: sources?.[4] || undefined,
    schemes: {
      light: Scheme.lightFromCorePalette(multiCorePalettes),
      dark: Scheme.darkFromCorePalette(multiCorePalettes),
    },
    palettes: {
      primary: multiCorePalettes.a1,
      secondary: multiCorePalettes.a2,
      tertiary: multiCorePalettes.a3,
      neutral: multiCorePalettes.n1,
      neutralVariant: multiCorePalettes.n2,
      error: multiCorePalettes.error,
    },
    customColors: customColors.map((c) => customColorContent(sources[0]!, c)),
  }
}

/**
 * Generate custom color group from source and target color
 *
 * @param source Source color
 * @param color Custom color
 * @return Custom color group
 *
 * @link https://m3.material.io/styles/color/the-color-system/color-roles
 */
function customColorContent(source: number, color: CustomColor): CustomColorGroup {
  let value = color.value
  if (color.blend) {
    value = Blend.harmonize(value, source)
  }
  const palette = CorePalette.contentOf(value)
  const tones = palette.a1
  return {
    color,
    value,
    light: {
      color: tones.tone(40),
      onColor: tones.tone(100),
      colorContainer: tones.tone(90),
      onColorContainer: tones.tone(10),
    },
    dark: {
      color: tones.tone(80),
      onColor: tones.tone(20),
      colorContainer: tones.tone(30),
      onColorContainer: tones.tone(90),
    },
  }
}

export const MT_TYPE_TYPE = ['Display', 'Headline', 'Title', 'Label', 'Body', 'Navigation Bar', 'Rich Text']

export const MT_SIZE = ['Large', 'Medium', 'Small']

export interface SizeOption {
  name: string
  label: string
  abr: string
  size: number
  line_height: number
  html_tag: string
}

export const MT_SIZE_OPTIONS: SizeOption[] = [
  { name: 'ExtraExtraLarge', label: 'Extra Extra Large', abr: 'XXL', size: 66, line_height: 73, html_tag: 'H1' },
  { name: 'ExtraLarge', label: 'Extra Large', abr: 'XL', size: 60, line_height: 67, html_tag: 'H2' },
  { name: 'Large', label: 'Large', abr: 'L', size: 57, line_height: 64, html_tag: 'H3' },
  { name: 'Medium', label: 'Medium', abr: 'M', size: 45, line_height: 52, html_tag: 'H4' },
  { name: 'Small', label: 'Small', abr: 'S', size: 36, line_height: 43, html_tag: 'H5' },
  { name: 'ExtraSmall', label: 'Extra Small', abr: 'XS', size: 30, line_height: 37, html_tag: 'H6' },
  { name: 'ExtraExtraSmall', label: 'Extra Extra Small', abr: 'XXS', size: 24, line_height: 31, html_tag: 'P' },
]

export const MT_HTML_TAGS = [
  { label: 'Heading 1 (h1)', value: 'H1' },
  { label: 'Heading 2 (h2)', value: 'H2' },
  { label: 'Heading 3 (h3)', value: 'H3' },
  { label: 'Heading 4 (h4)', value: 'H4' },
  { label: 'Heading 5 (h5)', value: 'H5' },
  { label: 'Heading 6 (h6)', value: 'H6' },
  { label: 'Paragraph (p)', value: 'Paragraph' },
  { label: 'Ordered List (ol)', value: 'Ol' },
  { label: 'Unordered List (ul)', value: 'Ul' },
]

export const MT_BREAKPOINTS = [
  { label: 'Mobile (Portrait)', value: 'phone' },
  { label: 'Mobile (Landscape)', value: 'phone-landscape' },
  { label: 'Tablet (Portrait)', value: 'tablet' },
  { label: 'Tablet (Landscape)', value: 'tablet-landscape' },
  { label: 'Desktop/Laptop', value: 'desktop' },
  { label: 'TV', value: 'tv' },
]

export const MT_COLOR_GROUP_KEYS: ColorGroupKey[] = ['color', 'onColor', 'colorContainer', 'onColorContainer']
export const MT_SCHEME_JSON_KEYS: SchemeJsonKey[] = [
  'background',
  'error',
  'errorContainer',
  'inverseOnSurface',
  'inversePrimary',
  'inverseSurface',
  'onBackground',
  'onError',
  'onErrorContainer',
  'onPrimary',
  'onPrimaryContainer',
  'onPrimaryFixed',
  'onPrimaryFixedVariant',
  'onSecondary',
  'onSecondaryContainer',
  'onSecondaryFixed',
  'onSecondaryFixedVariant',
  'onSurface',
  'onSurfaceVariant',
  'onTertiary',
  'onTertiaryContainer',
  'onTertiaryFixed',
  'onTertiaryFixedVariant',
  'outline',
  'outlineVariant',
  'primary',
  'primaryContainer',
  'primaryFixed',
  'primaryFixedDim',
  'scrim',
  'secondary',
  'secondaryContainer',
  'secondaryFixed',
  'secondaryFixedDim',
  'secondarySurface',
  'surface',
  'surfaceBright',
  'surfaceContainer',
  'surfaceContainerHigh',
  'surfaceContainerHighest',
  'surfaceContainerLow',
  'surfaceContainerLowest',
  'surfaceDim',
  'surfaceTint',
  'surfaceVariant',
  'tertiary',
  'tertiaryContainer',
  'tertiaryFixed',
  'tertiaryFixedDim',
  'tertiarySurface',
]

export function getTokenColor(tokenName: string, theme: Theme, mode: MtColorScheme): string | undefined {
  const scheme = theme.schemes[mode]

  // Handle title tokens
  if (tokenName?.endsWith('-title')) {
    return undefined // Skip coloring for title tokens
  }

  // Map our token names to scheme property names
  const tokenToSchemeMap: Record<string, keyof typeof scheme> = {
    primary: 'primary',
    'primary-container': 'primaryContainer',
    'on-primary': 'onPrimary',
    'on-primary-container': 'onPrimaryContainer',

    secondary: 'secondary',
    'secondary-container': 'secondaryContainer',
    'on-secondary': 'onSecondary',
    'on-secondary-container': 'onSecondaryContainer',

    tertiary: 'tertiary',
    'tertiary-container': 'tertiaryContainer',
    'on-tertiary': 'onTertiary',
    'on-tertiary-container': 'onTertiaryContainer',

    error: 'error',
    'error-container': 'errorContainer',
    'on-error': 'onError',
    'on-error-container': 'onErrorContainer',

    background: 'background',
    'on-background': 'onBackground',
    surface: 'surface',
    'on-surface': 'onSurface',
    'surface-variant': 'surfaceVariant',
    'on-surface-variant': 'onSurfaceVariant',
    'outline-variant': 'outlineVariant',
  }

  // Handle custom extended colors
  if (tokenName?.startsWith('custom-extended-')) {
    const parts = tokenName.split('-')
    const index = parseInt(parts[2]) - 1

    if (index >= 0 && index < (theme.customColors?.length || 0)) {
      const customColor = theme.customColors[index]

      // Handle different types of extended color tokens
      if (tokenName.endsWith('-source')) {
        return hexFromArgb(customColor.value)
      }

      const modeColors = customColor[mode]
      if (tokenName.endsWith('-color')) {
        return hexFromArgb(modeColors.color)
      }
      if (tokenName.endsWith('-color-container')) {
        return hexFromArgb(modeColors.colorContainer)
      }
      if (tokenName.endsWith('-on-color')) {
        return hexFromArgb(modeColors.onColor)
      }
      if (tokenName.endsWith('-on-color-container')) {
        return hexFromArgb(modeColors.onColorContainer)
      }
    }
    return undefined
  }

  // Handle standard system colors
  const schemeKey = tokenToSchemeMap[tokenName]
  if (schemeKey && scheme[schemeKey]) {
    const value = typeof scheme[schemeKey] === 'function' ? scheme[schemeKey]() : scheme[schemeKey]
    return hexFromArgb(value as number)
  }

  return undefined
}

export function getColorSetName(themes: Array<{ name: string; theme: MultiSourceTheme }>, index: number): string {
  const name = `Set ${index}`
  return themes.findIndex((theme) => theme.name === name) === -1 ? name : getColorSetName(themes, index + 1)
}

export function getThemeHexColorWithSource(theme: Theme) {
  const source = hexFromArgb(theme.source)
  const primary = hexFromArgb(theme.palettes.primary.tone(60))
  const secondary = hexFromArgb(theme.palettes.secondary.tone(60))
  const tertiary = hexFromArgb(theme.palettes.tertiary.tone(60))
  const neutral = hexFromArgb(theme.palettes.neutral.tone(60))
  return { source, primary, secondary, tertiary, neutral }
}

export function getThemeHexColorScheme(theme: Theme, mode = 'light') {
  const primary = hexFromArgb(theme.schemes[mode as 'light']?.primary)
  const primaryContainer = hexFromArgb(theme.schemes[mode as 'light']?.primaryContainer)
  const secondary = hexFromArgb(theme.schemes[mode as 'light']?.secondary)
  const tertiary = hexFromArgb(theme.schemes[mode as 'light']?.tertiary)

  return { primary, primaryContainer, secondary, tertiary }
}

export const convertToRem = (value: number | string): number => {
  const maxRem = 10

  // Handle string input
  if (typeof value === 'string') {
    // Remove 'rem' suffix if exists
    const cleanValue = value.replace('rem', '').trim()
    const numValue = parseFloat(cleanValue)
    if (isNaN(numValue)) return 0
    value = numValue
  }

  // Handle number input
  if (value < 0) return 0
  const remValue = value !== 0 && value > maxRem ? value / 16 : value
  return parseFloat(remValue?.toFixed(2))
}

function generateExtendedColorToken(index: number, type: 'bg' | 'fg' | 'both') {
  let colors = [`custom-extended-${index}-source`]

  if (type === 'bg' || type === 'both') {
    colors.push(`custom-extended-${index}-color`)
    colors.push(`custom-extended-${index}-color-container`)
  }
  if (type === 'fg' || type === 'both') {
    colors.push(`custom-extended-${index}-on-color`)
    colors.push(`custom-extended-${index}-on-color-container`)
  }

  return colors
}

export const BRANDKIT_ALL_COLOR_OPTIONS = [
  'outline',
  'outline-variant',
  'primary',
  'on-primary',
  'secondary',
  'on-secondary',
  'secondary-container',
  'on-secondary-container',
  'tertiary',
  'on-tertiary',
  'tertiary-container',
  'on-tertiary-container',
  'surface',
  'on-surface',
  'surface-variant',
  'on-surface-variant',
  // 'surface-container',
]

// Text Color Modification

const isLightColor = (color: number) => {
  const hct = Hct.fromInt(color)
  return hct.tone >= 50
}

const isDarkColor = (color: number) => {
  const hct = Hct.fromInt(color)
  return hct.tone < 50
}

const isValidHexColor = (hexColor: string): boolean => {
  const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/

  if (!hexPattern.test(hexColor)) {
    return false
  }

  try {
    argbFromHex(hexColor) // If invalid, this will throw an error
    return true
  } catch (error) {
    return false
  }
}

/**
 * Generate adaptive text color based on background.
 * @deprecated replaced by getAdaptiveColorV2
 * @note the backgroundColor and overlayColor should be a material token color ('primary', 'surface', etc)
 */
const getAdaptiveColor = (
  backgroundColor?: string,
  finalOpacity = 0.98,
  overlayOpacity?: number,
  overlayColor?: string,
  currentBrandkitTheme?: Theme,
  mode?: MtColorScheme
) => {
  const hexColor =
    currentBrandkitTheme && mode
      ? getTokenColor(backgroundColor ?? 'surface', currentBrandkitTheme, mode)
      : getCssVariableValue(`var(--md-sys-color-${backgroundColor})`)

  if (!hexColor) return

  const argbBgColor = argbFromHex(isValidHexColor(hexColor) ? hexColor : '#00000000')

  try {
    // Handle background only
    const withOverlay = !!overlayColor && typeof overlayOpacity !== 'undefined'
    if (!withOverlay) {
      return isLightColor(argbBgColor) ? `rgba(0, 0, 0, ${finalOpacity})` : `rgba(255, 255, 255, ${finalOpacity})`
    }

    // Handle overlay case
    const baseHct = Hct.fromInt(argbBgColor)
    let blendedHct = baseHct
    if (!!overlayColor) {
      const opacity = typeof overlayOpacity !== 'undefined' ? overlayOpacity : 100
      const argbOverlayColor = argbFromHex(getCssVariableValue(`var(--md-sys-color-${overlayColor})`))

      const overlayHex = isDarkColor(argbOverlayColor)
        ? '#000000'
        : !isDarkColor(argbOverlayColor)
          ? '#ffffff'
          : overlayColor

      const overlayHct = Hct.fromInt(argbFromHex(overlayHex as string))

      const blendedTone = baseHct.tone + (overlayHct.tone - baseHct.tone) * (opacity / 100)

      blendedHct = Hct.from(
        baseHct.hue,
        Math.max(0, Math.min(baseHct.chroma - (opacity / 100) * baseHct.chroma, 150)),
        blendedTone
      )
    }
    return isLightColor(blendedHct.toInt()) ? `rgba(0, 0, 0, ${finalOpacity})` : `rgba(255, 255, 255, ${finalOpacity})`
    //
  } catch (error) {
    return 'rgba(0, 0, 0, 0.98)'
  }
}

export const getAdaptiveColorV2 = (
  backgroundColor?: string,
  finalOpacity = 0.98,
  overlayOpacity?: number,
  overlayColor?: string
) => {
  const hexColor = getCssVariableValue(`var(--color-on-${backgroundColor})`)

  if (!hexColor) return

  const argbBgColor = argbFromHex(isValidHexColor(hexColor) ? hexColor : '#00000000')

  try {
    // Handle background only
    const withOverlay = !!overlayColor && typeof overlayOpacity !== 'undefined'
    if (!withOverlay) {
      return isLightColor(argbBgColor) ? `rgba(0, 0, 0, ${finalOpacity})` : `rgba(255, 255, 255, ${finalOpacity})`
    }

    // Handle overlay case
    const baseHct = Hct.fromInt(argbBgColor)
    let blendedHct = baseHct
    if (!!overlayColor) {
      const opacity = typeof overlayOpacity !== 'undefined' ? overlayOpacity : 100
      const argbOverlayColor = argbFromHex(getCssVariableValue(`var(--color-${overlayColor})`))

      const overlayHex = isDarkColor(argbOverlayColor)
        ? '#000000'
        : !isDarkColor(argbOverlayColor)
          ? '#ffffff'
          : overlayColor

      const overlayHct = Hct.fromInt(argbFromHex(overlayHex as string))

      const blendedTone = baseHct.tone + (overlayHct.tone - baseHct.tone) * (opacity / 100)

      blendedHct = Hct.from(
        baseHct.hue,
        Math.max(0, Math.min(baseHct.chroma - (opacity / 100) * baseHct.chroma, 150)),
        blendedTone
      )
    }
    return isLightColor(blendedHct.toInt()) ? `rgba(0, 0, 0, ${finalOpacity})` : `rgba(255, 255, 255, ${finalOpacity})`
    //
  } catch (error) {
    return 'rgba(0, 0, 0, 0.98)'
  }
}

const calculateToneFactor = (
  originalTone: number,
  backgroundTone: number,
  contrastMultiplier = 1 // New parameter for adjusting contrast
): number => {
  const targetToneOnLight = 20 * contrastMultiplier // Darker tone for light backgrounds
  const targetToneOnDark = 90 * contrastMultiplier // Lighter tone for dark backgrounds

  const targetTone = backgroundTone >= 50 ? targetToneOnLight : targetToneOnDark
  const toneDifference = Math.abs(originalTone - targetTone)

  // Increase contrast for middle tones
  if (originalTone >= 45 && originalTone <= 55) {
    return Math.min(toneDifference, 40 * contrastMultiplier)
  }

  // Increase contrast for extreme tones
  if (originalTone <= 15 || originalTone >= 85) {
    return Math.min(toneDifference, 90 * contrastMultiplier)
  }

  if (originalTone <= 20 || originalTone >= 80) {
    return Math.min(toneDifference, 80 * contrastMultiplier)
  }

  return Math.min(toneDifference, (20 + (toneDifference / 100) * 15) * contrastMultiplier)
}

/**
 * Generate adaptive text color from base color, based on background.
 *
 * @note
 *  - we cannot control the finalOpacity, since it will be autogenerated from the base color tone
 *  - the baseColor, backgroundColor and overlayColor should be a material token color ('primary', 'surface', etc)
 *
 * @deprecated
 */
const getAdaptiveColorFromBase = (
  baseColor: string,
  backgroundColor: string,
  overlayOpacity?: number,
  overlayColor?: string,
  currentBrandkitTheme?: Theme,
  mode?: MtColorScheme
) => {
  // Handle hex color from image directly
  const isBackgroundHexColor = backgroundColor?.startsWith('#')

  const baseHexColor = baseColor?.startsWith('#')
    ? baseColor
    : currentBrandkitTheme && mode
      ? getTokenColor(baseColor, currentBrandkitTheme, mode)
      : getCssVariableValue(`var(--md-sys-color-${baseColor})`)

  if (!baseHexColor) return

  const argbBaseColor = argbFromHex(isValidHexColor(baseHexColor) ? baseHexColor : '#00000000')

  // Handle background color - now accepts direct hex values from image
  const bgHexColor = isBackgroundHexColor
    ? backgroundColor
    : currentBrandkitTheme && mode
      ? getTokenColor(backgroundColor, currentBrandkitTheme, mode)
      : getCssVariableValue(`var(--md-sys-color-${backgroundColor})`)

  if (!bgHexColor) return

  const argbBackgroundColor = argbFromHex(isValidHexColor(bgHexColor) ? bgHexColor : '#00000000')

  try {
    const backgroundHct = Hct.fromInt(argbBackgroundColor)

    // Handle background only
    const withOverlay = !!overlayColor && typeof overlayOpacity !== 'undefined'
    if (!withOverlay) {
      const baseColorHct = Hct.fromInt(argbBaseColor)
      const isLightBg = isLightColor(argbBackgroundColor)
      const isLightBase = isLightColor(argbBaseColor)

      let finalHct = baseColorHct

      // Increase tone factor for image backgrounds
      const toneFactor = calculateToneFactor(
        baseColorHct.tone,
        backgroundHct.tone,
        isBackgroundHexColor ? 1.5 : 1 // Increase contrast for image backgrounds
      )

      // Adjust contrast more aggressively for image backgrounds
      if (isLightBg && isLightBase) {
        finalHct = Hct.from(
          baseColorHct.hue,
          baseColorHct.chroma,
          Math.max(0, baseColorHct.tone - (isBackgroundHexColor ? toneFactor * 1.2 : toneFactor))
        )
      } else if (!isLightBg && !isLightBase) {
        finalHct = Hct.from(
          baseColorHct.hue,
          baseColorHct.chroma,
          Math.min(100, baseColorHct.tone + (isBackgroundHexColor ? toneFactor * 1.2 : toneFactor))
        )
      }

      return hexFromArgb(finalHct.toInt())
    }

    const isOverlayColorHex = overlayColor?.startsWith('#')

    // Handle overlay case
    const overlayColorHex = isOverlayColorHex
      ? overlayColor
      : currentBrandkitTheme && mode
        ? getTokenColor(overlayColor, currentBrandkitTheme, mode)
        : getCssVariableValue(`var(--md-sys-color-${overlayColor})`)
    if (!overlayColorHex) return
    const argbOverlayColor = argbFromHex(overlayColorHex)
    const overlayHct = Hct.fromInt(argbOverlayColor)
    const opacity = overlayOpacity ?? 100

    // For significant overlay (opacity >= 30%), prioritize overlay color
    if (opacity >= 30) {
      const baseColorHct = Hct.fromInt(argbBaseColor)
      const isOverlayDark = overlayHct.tone < 50

      // If overlay is dark, make text light
      if (isOverlayDark) {
        return hexFromArgb(
          Hct.from(baseColorHct.hue, baseColorHct.chroma, Math.max(80, baseColorHct.tone + 40)).toInt()
        )
      }
    }

    // Calculate the blended background color
    const blendedChroma = Math.max(0, Math.min(backgroundHct.chroma - (opacity / 100) * backgroundHct.chroma, 150))
    const blendedTone = backgroundHct.tone + (overlayHct.tone - backgroundHct.tone) * (opacity / 100)
    const blendedHct = Hct.from(backgroundHct.hue, blendedChroma, blendedTone)

    // Modify the base color based on the blended background
    const baseColorHct = Hct.fromInt(argbBaseColor)
    const isLightBlended = isLightColor(blendedHct.toInt())
    const isLightBase = isLightColor(argbBaseColor)

    let finalHct = baseColorHct

    const toneFactor = calculateToneFactor(baseColorHct.tone, blendedHct.tone)

    // Only modify if the base color and blended background have similar brightness
    if (isLightBlended && isLightBase) {
      finalHct = Hct.from(baseColorHct.hue, baseColorHct.chroma, Math.max(0, baseColorHct.tone - toneFactor))
    } else if (!isLightBlended && !isLightBase) {
      finalHct = Hct.from(baseColorHct.hue, baseColorHct.chroma, Math.min(100, baseColorHct.tone + toneFactor))
    }

    // return final contrast color
    return hexFromArgb(finalHct.toInt())
  } catch (error) {
    return hexFromArgb(argbBaseColor)
  }
}

/**
 * @deprecated
 */
const getAdaptiveColorFromBaseV2 = (
  baseColor: string,
  backgroundColor: string,
  overlayOpacity?: number,
  overlayColor?: string,
  currentBrandkitTheme?: Theme,
  mode?: MtColorScheme
) => {
  // Handle hex color from image directly
  const isBackgroundHexColor = backgroundColor?.startsWith('#')

  const baseHexColor = baseColor?.startsWith('#')
    ? baseColor
    : currentBrandkitTheme && mode
      ? getTokenColor(baseColor, currentBrandkitTheme, mode)
      : getCssVariableValue(`var(--color-${baseColor})`)

  if (!baseHexColor) return

  const argbBaseColor = argbFromHex(isValidHexColor(baseHexColor) ? baseHexColor : '#00000000')

  // Handle background color - now accepts direct hex values from image
  const bgHexColor = isBackgroundHexColor
    ? backgroundColor
    : currentBrandkitTheme && mode
      ? getTokenColor(backgroundColor, currentBrandkitTheme, mode)
      : getCssVariableValue(`var(--color-${backgroundColor})`)

  if (!bgHexColor) return

  const argbBackgroundColor = argbFromHex(isValidHexColor(bgHexColor) ? bgHexColor : '#00000000')

  try {
    const backgroundHct = Hct.fromInt(argbBackgroundColor)

    // Handle background only
    const withOverlay = !!overlayColor && typeof overlayOpacity !== 'undefined'
    if (!withOverlay) {
      const baseColorHct = Hct.fromInt(argbBaseColor)
      const isLightBg = isLightColor(argbBackgroundColor)
      const isLightBase = isLightColor(argbBaseColor)

      let finalHct = baseColorHct

      // Increase tone factor for image backgrounds
      const toneFactor = calculateToneFactor(
        baseColorHct.tone,
        backgroundHct.tone,
        isBackgroundHexColor ? 1.5 : 1 // Increase contrast for image backgrounds
      )

      // Adjust contrast more aggressively for image backgrounds
      if (isLightBg && isLightBase) {
        finalHct = Hct.from(
          baseColorHct.hue,
          baseColorHct.chroma,
          Math.max(0, baseColorHct.tone - (isBackgroundHexColor ? toneFactor * 1.2 : toneFactor))
        )
      } else if (!isLightBg && !isLightBase) {
        finalHct = Hct.from(
          baseColorHct.hue,
          baseColorHct.chroma,
          Math.min(100, baseColorHct.tone + (isBackgroundHexColor ? toneFactor * 1.2 : toneFactor))
        )
      }

      return hexFromArgb(finalHct.toInt())
    }

    const isOverlayColorHex = overlayColor?.startsWith('#')

    // Handle overlay case
    const overlayColorHex = isOverlayColorHex
      ? overlayColor
      : currentBrandkitTheme && mode
        ? getTokenColor(overlayColor, currentBrandkitTheme, mode)
        : getCssVariableValue(`var(--color-${overlayColor})`)
    if (!overlayColorHex) return
    const argbOverlayColor = argbFromHex(overlayColorHex)
    const overlayHct = Hct.fromInt(argbOverlayColor)
    const opacity = overlayOpacity ?? 100

    // For significant overlay (opacity >= 30%), prioritize overlay color
    if (opacity >= 30) {
      const baseColorHct = Hct.fromInt(argbBaseColor)
      const isOverlayDark = overlayHct.tone < 50

      // If overlay is dark, make text light
      if (isOverlayDark) {
        return hexFromArgb(
          Hct.from(baseColorHct.hue, baseColorHct.chroma, Math.max(80, baseColorHct.tone + 40)).toInt()
        )
      }
    }

    // Calculate the blended background color
    const blendedChroma = Math.max(0, Math.min(backgroundHct.chroma - (opacity / 100) * backgroundHct.chroma, 150))
    const blendedTone = backgroundHct.tone + (overlayHct.tone - backgroundHct.tone) * (opacity / 100)
    const blendedHct = Hct.from(backgroundHct.hue, blendedChroma, blendedTone)

    // Modify the base color based on the blended background
    const baseColorHct = Hct.fromInt(argbBaseColor)
    const isLightBlended = isLightColor(blendedHct.toInt())
    const isLightBase = isLightColor(argbBaseColor)

    let finalHct = baseColorHct

    const toneFactor = calculateToneFactor(baseColorHct.tone, blendedHct.tone)

    // Only modify if the base color and blended background have similar brightness
    if (isLightBlended && isLightBase) {
      finalHct = Hct.from(baseColorHct.hue, baseColorHct.chroma, Math.max(0, baseColorHct.tone - toneFactor))
    } else if (!isLightBlended && !isLightBase) {
      finalHct = Hct.from(baseColorHct.hue, baseColorHct.chroma, Math.min(100, baseColorHct.tone + toneFactor))
    }

    // return final contrast color
    return hexFromArgb(finalHct.toInt())
  } catch (error) {
    return hexFromArgb(argbBaseColor)
  }
}

const borderRadiusMap = {
  full: '9999px',
  'extra-large': '16px',
  large: '12px',
  medium: '8px',
  small: '6px',
  'extra-small': '4px',
  none: '0',
}

export const getBorderRadius = (size: MdShape | undefined): string => {
  if (!size) return '0'
  return borderRadiusMap[size] || '0'
}

export const getCssVariableValue = (variable: string) => {
  if (variable.startsWith('#')) return variable

  const match = variable.match(/\(([^)]+)\)/)
  const varName = match ? match[1] : ''

  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
}

export const BASE_PALETTE_COLOR: { name: string; color: string }[] = [
  { name: 'Material 3', color: '#6750A4' },
  { name: 'Minty Fresh', color: '#1abc9c' },
  { name: 'Dodger Blue', color: '#3498db' },
  { name: 'Fiery Red', color: '#e74c3c' },
  { name: 'Regal Purple', color: '#9b59b6' },
  { name: 'Emerald City', color: '#2ecc71' },
  { name: 'Golden Sunshine', color: '#f1c40f' },
  { name: 'Slate Gray', color: '#95a5a6' },
  { name: 'Rebecca Purple', color: '#663399' },
  { name: 'Burnt Orange', color: '#d35400' },
  { name: 'Burgundy Wine', color: '#c0392b' },
  { name: 'Pink Sherbet', color: '#ff9ff3' },
  { name: 'Sun Yellow', color: '#fdcb6e' },
  { name: 'Turquoise Blue', color: '#00cec9' },
  { name: 'Deep Purple', color: '#8e44ad' },
  { name: 'Indian Red', color: '#ff6b6b' },
  { name: 'Saffron', color: '#feca57' },
  { name: 'Green Sea', color: '#00b894' },
  { name: 'Cloudy Gray', color: '#f1f2f6' },
  { name: 'Cerise', color: '#e84393' },
  { name: 'Blue-Violet', color: '#6c5ce7' },
]

interface GeneratePaletteStyleOptions<T extends 'string' | 'object'> {
  output: T
  theme: MultiSourceTheme
  mode: MtColorScheme
  typography?: MtTypography
  flag?: CSSKindFlag
}

/**
 * generate palette style from brand kit theme,
 * it will inject all material design tokens into css variables
 *
 * @deprecated
 */
export function generatePaletteStyle<T extends 'string' | 'object'>(
  options: GeneratePaletteStyleOptions<T>
): T extends 'string' ? string : Record<string, string> {
  const { output, theme, mode, typography, flag = CSSKindFlag.All } = options

  // get palette from brand kit theme
  const palettes = getApplyTheme(theme, { paletteTones: false, typography })

  // return stringified css or styles object
  return (output === 'string' ? cssObjToStr(palettes, mode, flag) : cssObjToProps(palettes, mode)) as any
}

export function isValidColor(color: string | undefined): boolean {
  if (!color) return false

  const style = new Option().style
  style.color = color
  const isValidNamedColor = style.color === color

  const isValidHexColor = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(color)

  const isValidRgbOrRgbaColor =
    /^(rgb|rgba)\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0(\.\d+)?|1(\.0+)?)\s*)?\)$/.test(color)

  const isValidHslOrHslaColor =
    /^(hsl|hsla)\(\s*\d{1,3}\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%\s*(,\s*(0(\.\d+)?|1(\.0+)?)\s*)?\)$/.test(color)

  return isValidNamedColor || isValidHexColor || isValidRgbOrRgbaColor || isValidHslOrHslaColor
}

/**
 * New color utils to convert color input into brandkit source color format (argb)
 *
 * @param {string | { hex: any; rgb: any; hsv: any }} color - could be hex string or object
 *
 * @returns {number} argb color
 */
export function colorToSource(color: string | { hex: any; rgb: any; hsv: any }) {
  const argbColor =
    typeof color === 'string' ? argbFromHex(color) : argbFromRgba({ ...color.rgb, a: Math.round(color.rgb.a * 255) })
  return argbColor
}

/**
 * New color utils, to convert source color (argb) into hex, supports alpha value
 *
 * @param {number} argb - argb color
 *
 * @returns {string} hex color
 */
export function sourceToHex(argb: number) {
  const { r, g, b, a } = rgbaFromArgb(argb)
  const [rr, gg, bb, aa] = [r, g, b, a].map((v) => Math.round(v).toString(16).padStart(2, '0'))
  return ['#', rr, gg, bb, aa === 'ff' ? undefined : aa].join('')
}
